/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { debugError, debugLog } from "../../helpers/debug";
import { useAuth } from "../auth";
import { useFirebase } from "../Firebase";
import { getObjFromLink, transformCollectionSnap } from "../helpers";
import { useKegs } from "../kegs";
import { formatClientData } from "./formatting";

const ClientsContext = createContext({});

export const ClientsProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims } = useAuth();
  const [clients, setClients] = useState();

  const { companyKegs } = useKegs();

  useEffect(() => {
    if (!claims?.companyLink) {
      debugLog("[ClientsProvider] No companyLink, skipping clients listener");
      return () => {};
    }

    debugLog("[ClientsProvider] Setting up clients listener");

    return firebase.clients(getObjFromLink(claims.companyLink).id).onSnapshot(
      snap => {
        setClients(
          transformCollectionSnap(snap).filter(client => !client.isArchive)
        );
      },
      err => debugLog(err)
    );
  }, [
    claims.companyLink,
    firebase,
  ]);

  const createClient = async client => {
    if (!claims?.companyLink) {
      debugError("[ClientsProvider] Cannot create client - no companyLink");
      return;
    }

    const newClient = formatClientData(client, claims.companyLink, true);

    return await firebase
      .clients(getObjFromLink(claims.companyLink).id)
      .add(newClient);
  };

  const updateClient = async (client, id) => {
    if (!claims?.companyLink) {
      debugError("[ClientsProvider] Cannot update client - no companyLink");
      return;
    }

    const updatedClient = formatClientData(client, claims.companyLink);
    const batch = firebase.db.batch();

    const clientDocRef = firebase
      .clients(getObjFromLink(claims.companyLink).id)
      .doc(id);

    await batch.update(clientDocRef, updatedClient);

    const newClientData = {
      clientLink: `${client.id}__${client.name}`,
      clientObj: {
        id: client.id,
        name: client.name,
      },
    };

    const kegsQuerySnap = await firebase.db
      .collection("kegs")
      .where("clientObj.id", "==", id)
      .where("isArchive", "==", false)
      .get();

    const kegs = transformCollectionSnap(kegsQuerySnap);

    kegs.forEach(k => {
      batch.update(firebase.db.collection("kegs").doc(k.id), newClientData);
    });

    await batch.commit();
  };

  const archiveClient = async (isArchive, id) => {
    if (!claims?.companyLink) {
      debugError("[ClientsProvider] Cannot archive client - no companyLink");
      return;
    }

    return await firebase
      .clients(getObjFromLink(claims.companyLink).id)
      .doc(id)
      .update({
        dateUpdated: new Date(),
        isArchive: !isArchive,
      });
  };

  const clientStats = useMemo(() => {
    const stockWithClients = companyKegs
      ?.filter(k => !k.isArchived)
      .filter(
        item => item.clientLink && item && item.kegStatus === "dispatched"
      );

    if (clients && companyKegs) {
      return clients
        ?.map(client => {
          const stockTypeKeg = stockWithClients.filter(
            item => item.clientObj.id === client.id && item.type === "keg"
          );

          const stockTypeGas = stockWithClients.filter(
            item => item.clientObj.id === client.id && item.type === "gas"
          );

          const stockTypeFridges = stockWithClients.filter(
            item => item.clientObj.id === client.id && item.type === "fridge"
          );

          const allClientStock = stockWithClients.filter(
            item => item.clientObj.id === client.id
          );

          let score = 0;

          if (client.name) {
            score = score + 5;
          }

          if (client.email) {
            score = score + 5;
          }

          if (client.phone) {
            score = score + 5;
          }

          if (client.website) {
            score = score + 5;
          }

          if (client.address_line1) {
            score = score + 10;
          }

          if (client.address_line2) {
            score = score + 5;
          }

          if (client.address_suburb) {
            score = score + 10;
          }

          if (client.address_city) {
            score = score + 10;
          }

          if (client.address_province) {
            score = score + 10;
          }

          if (client.address_postalCode) {
            score = score + 5;
          }

          if (client.gps_lat) {
            score = score + 1;
          }

          if (client.gps_lng) {
            score = score + 1;
          }

          if (client.contact_person_name) {
            score = score + 10;
          }

          if (client.contact_person_position) {
            score = score + 2;
          }

          if (client.contact_person_email) {
            score = score + 1;
          }

          if (client.contact_person_phone) {
            score = score + 10;
          }

          if (client.contact_person_mobile) {
            score = score + 5;
          }

          return {
            countAllStock: allClientStock.length,
            countFridges: stockTypeFridges.length,
            countGas: stockTypeGas.length,
            countKegs: stockTypeKeg.length,
            fridges: stockTypeFridges,
            gas: stockTypeGas,
            id: client.id,
            kegs: stockTypeKeg,
            name: client.name,
            score,
          };
        })
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    }
  }, [
    clients,
    companyKegs,
  ]);

  return (
    <ClientsContext.Provider
      value={{
        archiveClient,
        clientStats,
        clients,
        createClient,
        updateClient,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};

export const ClientsConsumer = ClientsContext.Consumer;

export const useClients = () => useContext(ClientsContext);
